import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import ru from './locales/ru.json';
import de from './locales/de.json';
import { api } from '@/api/client';

export type LocaleKey = string;

const STORAGE_KEY = 'pro-kanban.locale';
en.app.name = ru.app.name = de.app.name = __APP_NAME__;

/**
 * Locale resolution: saved preference → browser language → instance default.
 * The signed-in user's preference overrides this as soon as the profile loads.
 */
function initialLocale(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) return saved;

  const supported = __SUPPORTED_LOCALES__;
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const short = candidate.toLowerCase().split('-')[0];
    if (supported.includes(short)) return short;
  }
  return __DEFAULT_LOCALE__;
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initialLocale(),
  fallbackLocale: 'en',
  missingWarn: false,
  fallbackWarn: false,
  messages: { en, ru, de } as never,
  // Russian needs one/few/many; vue-i18n resolves the index from this rule.
  pluralRules: {
    ru: (choice: number, choicesLength: number): number => {
      if (choice === 0) return 0;
      const mod10 = choice % 10;
      const mod100 = choice % 100;
      if (mod10 === 1 && mod100 !== 11) return 1;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 2;
      return choicesLength < 4 ? 2 : 3;
    },
  },
});

export const supportedLocales = __SUPPORTED_LOCALES__;

export const localeNames: Record<string, string> = {
  en: 'English',
  ru: 'Русский',
  de: 'Deutsch',
};

/**
 * Locale/company pairs whose backend catalogue has already been merged. The
 * company is part of the key because operator overrides are scoped to one.
 */
const hydrated = new Set<string>();

const stampFor = (locale: string, companyId?: string): string => `${locale}:${companyId ?? ''}`;

/**
 * Switches the interface language instantly — no reload.
 * Also pulls the backend catalogue so server-side keys (errors, notifications,
 * plugin strings) and any operator-defined overrides are available.
 *
 * Bundling a locale is not a reason to skip that fetch: the bundle covers the
 * SPA's own strings and nothing the server owns, so every `plugin.*` namespace
 * lives only in the backend catalogue. Guarding on `availableLocales` — which
 * always contains the three bundled locales — meant it was never fetched, and a
 * plugin's sidebar entry fell back to displaying its raw key.
 */
export async function setLocale(locale: string, options: { persist?: boolean; companyId?: string } = {}): Promise<void> {
  if (!supportedLocales.includes(locale)) locale = __DEFAULT_LOCALE__;

  // Switched before the fetch rather than after it: the bundled catalogue
  // already covers the interface, so the language changes on the click instead
  // of a round trip later, and the header below is set before the first request
  // can go out without it. The server-owned keys arrive a moment later and
  // vue-i18n re-renders whatever was showing a fallback.
  (i18n.global.locale as unknown as { value: string }).value = locale;
  document.documentElement.setAttribute('lang', locale);
  if (options.persist !== false) localStorage.setItem(STORAGE_KEY, locale);

  // Backend responses (validation, notification bodies) follow this header.
  api.setLocale(locale);

  if (!hydrated.has(stampFor(locale, options.companyId))) {
    await loadBackendCatalogue(locale, options.companyId);
  }
}

/**
 * Merges the backend catalogue into the bundled one for a locale. Always
 * fetches — callers that only want it once go through `setLocale`, and the
 * localization screen calls this directly to pick up an override it just saved.
 */
export async function loadBackendCatalogue(locale: string, companyId?: string): Promise<void> {
  try {
    const catalogue = await api.get<Record<string, unknown>>(`i18n/catalogue/${locale}`, companyId ? { companyId } : undefined);
    const existing = (i18n.global.getLocaleMessage(locale) ?? {}) as Record<string, unknown>;
    i18n.global.setLocaleMessage(locale, deepMerge(existing, toI18nPlaceholders(catalogue)) as never);
    hydrated.add(stampFor(locale, companyId));
  } catch {
    // The app stays usable on the bundled catalogue alone, and a later switch
    // retries because the failure left no stamp behind.
  }
}

/** Fetches the locales this instance actually supports, including added ones. */
export async function refreshSupportedLocales(): Promise<string[]> {
  try {
    const response = await api.get<{ locales: string[]; default: string }>('i18n/locales');
    return response.locales;
  } catch {
    return supportedLocales;
  }
}

/**
 * Rewrites the server's `{{ name }}` placeholders as vue-i18n's `{name}`.
 *
 * The backend renders its own catalogue — mail bodies, push payloads — with
 * mustache, so its strings arrive in that dialect. vue-i18n reads `{{name}}` as
 * a placeholder opened inside a placeholder and refuses to compile it; the
 * error carries no text in a production build, so it reaches the user as a bare
 * `SyntaxError: 9` and takes down whatever was rendering. That is one screen
 * away at all times: the notification preferences matrix compiles eleven of
 * these on mount, and every `ApiError` is shown through `t(errors.<code>)`.
 *
 * Converting on the way in lets each side keep its own dialect and makes the
 * params that already ride along with an `ApiError` interpolate as intended.
 * Messages the server already writes with single braces pass through untouched.
 */
function toI18nPlaceholders<T>(value: T): T {
  if (typeof value === 'string') return value.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, '{$1}') as T;
  if (Array.isArray(value)) return value.map(toI18nPlaceholders) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, toI18nPlaceholders(entry)])) as T;
  }
  return value;
}

function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { ...target };
  for (const [key, value] of Object.entries(source)) {
    if (value && typeof value === 'object' && !Array.isArray(value) && typeof result[key] === 'object' && result[key] !== null) {
      result[key] = deepMerge(result[key] as Record<string, unknown>, value as Record<string, unknown>);
    } else if (result[key] === undefined) {
      // Bundled UI strings win; the backend fills the gaps.
      result[key] = value;
    }
  }
  return result;
}

/**
 * Translate outside a component (stores, interceptors).
 *
 * The `i18n.global` object is narrowed through `unknown` on purpose: touching
 * `i18n.global.t` directly makes TypeScript expand the entire message-schema
 * union and exceed the instantiation depth limit.
 */
export function translate(key: string, params?: Record<string, unknown>): string {
  const global = i18n.global as unknown as { t: (key: string, params?: Record<string, unknown>) => string };
  return global.t(key, params);
}
